import * as React from "react";
import {autorun, reaction} from "mobx";
import {observer} from "mobx-react";

import {Message} from "semantic-ui-react";

import "./layer-list.scss";

import EsriLayerListViewModel from "@arcgis/core/widgets/LayerList/LayerListViewModel";

import {UiState} from "../../ui-state";
import {LayerListConfiguration} from "../../config/layer-list-configuration";

import {SyntheticEvent} from "react";
import {LayerListList} from "./layer-list-list/layer-list-list";
import {SidebarControlsConfiguration} from "../../../config/sidebar-configuration";
import {Legend as CustomLegend} from "../../../components/app/sidebar/legend/legend";
import {Legend as CoreLegend} from "../../../core/components/legend/legend";
import {AppState} from "../../../components/app/app-mobx";
import {TopicLayersConfiguration} from "../../../config/topic-configuration";
import {PathUtils} from "../../../utils/path-utils";
import _ from "lodash";
import WatchHandle = __esri.WatchHandle;
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import * as arcade from "@arcgis/core/arcade";
import {ListItemConfiguration} from "../../config/list-item-configuration";
import {MessageConfiguration} from "../../config/message-configuration";
import ListItem from "@arcgis/core/widgets/LayerList/ListItem";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import Collection from "@arcgis/core/core/Collection";
import ListItemProperties = __esri.ListItemProperties;
import {MapState} from "../../map-state";
import {Icon} from "../icon/icon";
import Sublayer from "@arcgis/core/layers/support/Sublayer";
import {AppConfiguration} from "../../config/app-configuration";

export interface LayerListProps {
    config: LayerListConfiguration;
    controlsConfig: SidebarControlsConfiguration;
    appConfig: AppConfiguration;
    appStore: AppState;
    mapStore: MapState;
    view: __esri.MapView | __esri.SceneView | undefined;
    onLoad?: (layerListViewModel: EsriLayerListViewModel) => any;
    uiStore: UiState;
    layers?: TopicLayersConfiguration;
    small?: boolean;
}
// Layer um index und uid ergänzen. Der Index wird zum Sortierung verwendet
interface ExtendedLayer extends __esri.Layer {
    index: number;
    limitLevel?: number;
    uid: string;
}
interface ExtendedSublayer extends Sublayer {
    index: number;
    children: number[];
    limitLevel?: number;
    uid: string;
    visibilityMode: string;
}

@observer
export class LayerList extends React.Component<LayerListProps, any> {
    autorunDisposers: any[] = [];
    watchHandles: WatchHandle[] = [];
    widgetKey = "layer-list";
    itemsConfig: ListItemConfiguration[] = [];
    itemVisibility = new Map<string, boolean>();
    operationalItems: Collection<ListItem>;
    groupOpenStatusSet: string[] = [];
    layerIds: string[] = []; // Layer IDs für Debug Konfiguration
    nextGroupId = 1000;
    constructor(props: LayerListProps) {
        super(props);

        this.state = {
            hasGroups: false,
            isLoading: null,
            postMessages: [],
            preMessages: [],
            viewModel: null,
            operationalItemsArray: null,
            regroupedItems: new Map<string, ListItem>()
        };
        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
        }
        this.reformatListItemWhenCreated = this.reformatListItemWhenCreated.bind(this);
        this.handleListItemClick = this.handleListItemClick.bind(this);
    }

    /**
     * Remember the state of the list items, so changes can trigger re renderings on child components
     *
     * @param event
     * @param eventData
     * @param listItem
     */
    handleListItemClick(event: SyntheticEvent, eventData: object, listItem: __esri.ListItem): void {
        event.preventDefault();

        const visible = eventData["checked"];

        this.setState(() => {
            const allItems = this.operationalItems.flatten((ite: __esri.ListItem) =>
                ite.children
            );
            const oi = allItems.find((item: __esri.ListItem) =>
                item.layer.id === listItem.layer.id || item.layer.get("uid") === listItem.layer.get("uid")
            );
            if (!oi) {
                return null;
            }
            // Nach Parent Layer überprüfen und für nicht GroupLayer visibilityMode implementieren
            if(oi.parent?.layer && oi.parent?.layer.type!=="group") {
                const parentVisibilityMode = oi.parent?.layer.get("visibilityMode");
                if (parentVisibilityMode === "exclusive") {
                    // Im visibilityMode exclusive zuerst alle "children" der Gruppe auf invisible setzten
                    if (visible) {
                        oi.parent.children.forEach((i) => {
                            if(oi.layer.get("uid") !== i.layer.get("uid")) {
                                i.layer.visible = false;
                                // Allfällige dazugehörende "children" auf invisible setzten
                                i.children.flatten((ite: __esri.ListItem) => ite.children).forEach((subI)=> {
                                    this.itemVisibility.set(subI.layer.get("uid"), subI.layer.visible);
                                    subI.layer.visible = false;
                                });
                            }
                        });
                    }
                } else if (parentVisibilityMode === "inherited") {
                    // Im visibilityMode inherited "parent" und alle "children" der Gruppe auf den selben Wert setzten
                    oi.parent.layer.visible = visible;
                    oi.parent.children.forEach((i) => {
                        i.layer.visible = visible;
                    });
                } else if (oi.layer.get("visibilityMode") === "inherited") {
                    // Im visibilityMode inherited alle "children" der Gruppe auf den selben Wert setzten
                    oi.children.forEach((i) => {
                        i.layer.visible = visible;
                        this.itemVisibility.set(i.layer.get("uid"), visible);
                    });
                }
            }
            this.itemVisibility.set(oi.layer.get("uid"), visible);
            if (visible && oi.layer.get("visibilityMode") === "exclusive" && oi.children.length > 0) {
                // Sicherstellen, dass im exclusive Modus jeweils nur ein "Child" aktive ist
                let visibleChildren=0;
                oi.children.forEach((i) => {
                    if(typeof this.itemVisibility.get(i.layer.get("uid")) === "boolean"
                        && this.itemVisibility.get(i.layer.get("uid"))) {
                        visibleChildren++;
                    }
                });
                if(visibleChildren!==1) {
                    oi.children.forEach((i, index) => {
                        this.itemVisibility.set(i.layer.get("uid"), index===0);
                    });
                }
            }
            oi.children.flatten((ite: __esri.ListItem) => ite.children).forEach((i)=>{
                if(!visible) {
                    this.itemVisibility.set(i.layer.get("uid"), i.layer.visible);
                    i.layer.visible = visible;
                } else if(typeof this.itemVisibility.get(i.layer.get("uid")) === "boolean") {
                    i.layer.visible = this.itemVisibility.get(i.layer.get("uid")) as boolean;
                } else {
                    i.layer.visible = visible;
                }
            });
            oi.visible = visible;
            oi.layer.visible = visible;
            // übergeordnete Layer auf sichtbar setzen
            if(visible && oi.parent) {
                let p = oi.parent;
                while (p) {
                    if (p.layer.visible !== visible) {
                        p.layer.visible = visible;
                        p.visible = visible;
                    }
                    p = p.parent;
                }
            }
            return {
                operationalItemsArray: this.operationalItems.flatten((ite: __esri.ListItem) =>
                    ite.children
                ).toArray()
            };
        });
    }
    handleLayerChange(layers?: TopicLayersConfiguration): void {
        const {config} = this.props;
        if(typeof layers === "undefined" || !this.state.operationalItemsArray){
            return;
        }
        const allItems = this.operationalItems.flatten((ite: __esri.ListItem) =>
            ite.children
        );
        if(layers.visible && layers.visible.length>0) {
            allItems.forEach((item: __esri.ListItem) => {
                const url = item.layer.get("url");
                if (typeof url === "string") {
                    const metaId = PathUtils.getMetaId(url);
                    const layerId = PathUtils.getLayerId(url);
                    if (typeof layerId === "number") {
                        if (layers.visible.some((layer) => layer.id === metaId
                            && (layer.layerId === layerId || (layer.layerIds && layer.layerIds.includes(layerId))))) {
                            item.visible = true;
                            item.layer.visible = true;
                            let p = item.parent;
                            while (p) {
                                if (!p.layer.visible) {
                                    p.layer.visible = true;
                                    p.visible = true;
                                }
                                p = p.parent;
                            }
                        } else {
                            item.visible = false;
                            item.layer.visible = false;
                        }
                    }
                }
            });
        }
        if(layers.open && Array.isArray(layers.open)) {
            if(Array.isArray(config.groups)){
                config.groups.forEach((itemConfig: ListItemConfiguration) => {
                    const groupId = this.props.uiStore.sublayerMap.get(itemConfig.layer.id as string);
                    const groupItem = allItems.find((item: __esri.ListItem) =>
                        item.layer.get("uid") === groupId
                    );
                    if(groupItem) {
                        groupItem.open = layers.open?.some((o)=> o.id === itemConfig.layer.id) as boolean;
                    }
                });
            }
        }
        this.reorder();
    }

    reformatListItemWhenCreated(event: any): void {
        // The event object contains an item property.
        // It is a ListItem referencing the associated layer
        // and other properties. You can control the visibility of the
        // item, its title, and actions using this object.

        const item = event.item;
        let url = item.layer.get("parsedUrl")?.path;
        if (!url) {
            url = item.layer.get("url"); // MapImageLayer Service
        }
        let meta = "";
        if (typeof url === "string") {
            const metaId = PathUtils.getMetaId(url);
            const layerId = PathUtils.getLayerId(url);
            if (typeof layerId === "number"){
                meta = metaId + "-" + layerId;
            } else {
                meta = metaId;
            }
        }
        if(this.props.appConfig.mode !== "production"){
            this.layerIds.push(meta);
            this.layerIds.push(item.layer.id);
        }
        // is there a configuration for this item?
        const config = this.itemsConfig.find((itemConfig: ListItemConfiguration) =>
            itemConfig.layer.id === item.layer.id || itemConfig.layer.id === meta
        );
        if (!config) {
            if(this.props.config.rebuild && item.layer.type !== "map-image"){
                if(typeof item.layer.index === "undefined") {
                    item.layer.listMode = "hide";
                }
                // Nicht konfigurierte Layer ausblenden
                item.layer.visible = false;
            }
            this.itemVisibility.set(item.layer.get("uid"), item.layer.visible);
            // nothing to do for this item
            return;
        }
        config.id = item.layer.id; // Layer an Konfiguration zuweisen.
        // see if layer item should be hidden
        if (config.hasOwnProperty("layer")) {
            if (config.layer.hasOwnProperty("listMode")) {
                item.layer.listMode = config.layer.listMode;
                if (config.layer.listMode === "hide") {
                    // also hide layer in the map
                    item.layer.visible = false;
                }
                // TODO: überprüfen, ob alle sublayers sichtbar geschaltet werden sollen, oder ob settings aus
                //  webmap übernommen werden sollen
                // if (config.layer.listMode === "hide-children") {
                //     item.children.forEach((child: __esri.ListItem) => {
                //         child.layer.visible = true;
                //     });
                // }
            }
            if (config.layer.hasOwnProperty("visible")) {
                item.layer.visible = config.layer.visible;
            }
            if (config.layer.hasOwnProperty("metauid")) {
                item.metauid = config.layer.metauid;
                item.layer.metauid = config.layer.metauid;
            }
        }
        // check if layer has a title
        if (config.hasOwnProperty("title")) {
            item.title = config.title;
            item.layer.title = config.title;
        }
        if (config.hasOwnProperty("index")) {
            item.index = config.index;
            item.layer.index = config.index;
        }

        // see if visibility of item has to be changed
        if (config.hasOwnProperty("visible")) {
            item.visible = config.visible;
            // set layer invisible too
            item.layer.visible = config.visible;
        }
        // TODO: add actions to layers (e.g. transparency slider)
    }
    updateMessages(){
        const {controlsConfig} = this.props;
        const preMessages = this.prepareMessagesContent(controlsConfig.layerList.preContent);
        Promise.all(preMessages).then((msgs) => {
            this.setState({preMessages: msgs.filter((m) => m)});
        });
        const messages = this.prepareMessagesContent(controlsConfig.layerList.postContent);
        Promise.all(messages).then((msgs) => {
            this.setState({postMessages: msgs.filter((m) => m)});
        });
    }
    prepareMessagesContent(messagesConfig?: MessageConfiguration[]): Array<Promise<JSX.Element | undefined>> {
        const messagePromises: Array<Promise<JSX.Element | undefined>> = [];
        if (messagesConfig && Array.isArray(messagesConfig)) {
            const profile: __esri.Profile = {
                variables: [{
                    name: "$activeLayers",
                    type: "array",
                    elementType: {
                        type: "text"
                    }
                }, {
                    name: "$subtopicId",
                    type: "text"
                }]
            };
            messagesConfig.forEach((message, index) => {
                if (typeof message.condition === "undefined") {
                    messagePromises.push(new Promise((resolve) => {
                        resolve(this.prepareMessageContent(message, index));
                    }));
                } else {
                    if(this.props.mapStore.activeLayers) {
                        const arcadeExecutor = arcade.createArcadeExecutor(message.condition, profile);
                        messagePromises.push(new Promise((resolve) => {
                            arcadeExecutor.then((ae) => {
                                const condition = ae.execute({
                                    $activeLayers: this.props.mapStore.activeLayers,
                                    $subtopicId: (this.props.appStore.subtopicId? this.props.appStore.subtopicId: "")
                                });
                                let m: JSX.Element | undefined;
                                if (condition) {
                                    m = this.prepareMessageContent(message, index);
                                }
                                resolve(m);
                            });
                        }));
                    }
                }
            });
        }
        return messagePromises;
    }
    prepareMessageContent(messageConfig: MessageConfiguration, index: number): JSX.Element {
        let message: JSX.Element;
        // quick and dirty!
        const messageContent = <p
            dangerouslySetInnerHTML={{__html: messageConfig.content}}/>;
        if (messageConfig.class === "blank"){
            message = <div
                key={index}
                className={messageConfig.class ? messageConfig.class : undefined}
            >{messageContent}</div>;
        } else {
            message = <div className={"geo-message-wrapper"} key={index}><Message
                attached="bottom"
                className={messageConfig.class ? messageConfig.class : undefined}
                content={messageContent}
            /></div>;
        }
        return message;
    }
    render(): JSX.Element | null {
        if (this.state.isLoading === null || this.state.isLoading) {
            return <div className={"geo-loading-wrapper"}><Icon name={"recycle"} className={"geo-rotating"} /></div>;
        }
        if (!this.state.viewModel) {
            return (
                <Message negative>
                    <Message.Header>Tut uns leid, der Karteninhalt konnte nicht ausgelesen werden.</Message.Header>
                    <p>Fehler beim Laden des ViewModels.</p>
                </Message>
            );
        }

        const {config, controlsConfig, small, view, appStore, mapStore, uiStore} = this.props;
        const {hasGroups, operationalItemsArray, postMessages, preMessages} = this.state;

        if (!operationalItemsArray) {
            return null;
        }

        let legend;
        if (controlsConfig.legend.position === "toc") {
            if (controlsConfig.legend.useEsri && controlsConfig.legend.esriConfig) {
                legend = (<CoreLegend
                    uiStore={uiStore}
                    view={view}
                    config={controlsConfig.legend.esriConfig}
                    controlsConfig={controlsConfig}
                    appStore={appStore}
                    mapStore={mapStore}
                    small={!!small}/>);
            } else {
                legend = (<CustomLegend
                    appStore={appStore}
                    uiStore={uiStore}
                    config={controlsConfig.legend} />);
            }
        }
        const isRoot = (controlsConfig.layerList.startLevel === 0);
        return (
            <div>
                <div className={"geo-layer-list"+(hasGroups? " geo-list-with-groups": "")}>
                    {preMessages}
                    <LayerListList
                        listItems={this.operationalItems.toArray()}
                        isRoot={isRoot}
                        level={0}
                        onListItemClick={this.handleListItemClick}
                        config={config}
                        small={small}
                        view={view}
                        uiStore={uiStore}
                        appStore={appStore}
                        mapStore={mapStore}
                        controlsConfig={controlsConfig} />
                    {legend}
                    {postMessages}
                </div>
                <div className="geo-layer-list-footer"/>
            </div>
        );
    }

    componentDidMount(): void {
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((reaction: any) => {
            const {config, layers, uiStore} = this.props;
            if (uiStore.widgets.has(this.widgetKey)) {
                reaction.dispose(); // stop autorun
                return;
            }
            // index in items ergänzen, falls keiner vorhanden ist.
            this.itemsConfig = config.items.map((item, index) => {
                if(!item.hasOwnProperty("index")) {
                    item.index = index + 1;
                }
                return item;
            });
            const viewModel = this.loadViewModel();
            if (!viewModel) {
                return;
            }
            this.operationalItems = viewModel.operationalItems;
            this.setState({
                viewModel,
                operationalItemsArray: this.operationalItems.flatten((ite: __esri.ListItem) =>
                    ite.children
                )
            });

            if (viewModel.state !== "ready") {
                this.setState({
                    isLoading: true
                });
            }

            this.watchHandles.push(reactiveUtils.when(
                () => viewModel.state === "ready",
                () => {
                    this.setState({
                        isLoading: false
                    });

                    this.addItemsToGroup(viewModel.operationalItems);
                    this.handleLayerChange(layers);
                },
                { initial: true, once: true }
            ));

            this.watchHandles.push(viewModel.watch("operationalItems.length",
                (nValue, oValue, pName: string, targetView: EsriLayerListViewModel) => {
                    const {operationalItems} = targetView;
                    this.operationalItems = operationalItems;
                    this.setState({
                        isLoading: false,
                        operationalItemsArray: this.operationalItems.flatten((ite: __esri.ListItem) =>
                            ite.children
                        )
                    });

                    // watch for visibility changes
                    this.operationalItems.forEach((listItem) => {
                        this.watchHandles.push(listItem.watch("layerView",
                            (newValue: any, oldValue: any, propertyName: string, target: __esri.ListItem) =>  {
                                target.watch("visible",
                                    () => {
                                        this.setState(() => ({
                                            isLoading: false
                                        }));
                                    });
                            })
                        );
                    });

                    this.addItemsToGroup(operationalItems);

                    if (this.props.onLoad) {
                        // call the hook
                        this.props.onLoad(viewModel);
                    }
                })
            );
            this.loadGroups(viewModel.operationalItems);
            uiStore.addWidget(this.widgetKey, this);
        }));
        this.updateMessages();
        this.autorunDisposers.push(reaction(() => this.props.mapStore.activeLayers, () => {
            this.updateMessages();
        }));
        this.autorunDisposers.push(reaction(() => this.props.appStore.subtopic?.revision, () => {
            if(!this.state.isLoading) {
                this.handleLayerChange(this.props.layers);
            }
        }));
    }
    componentWillUnmount(): void {
        const {uiStore} = this.props;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        this.watchHandles.forEach((handle: WatchHandle) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        uiStore.removeWidget(this.widgetKey);
    }
    componentDidUpdate(prevProps: any) {
        const {layers} = this.props;
        if (!_.isEqual(prevProps.layers, layers)){
            this.handleLayerChange(layers);
        }
    }
    loadViewModel(): EsriLayerListViewModel | null {
        const {view} = this.props;
        if (!view) {
            return null;
        }

        const config = this.props.config as __esri.LayerListProperties;
        const props = {
            view,
            listItemCreatedFunction: this.reformatListItemWhenCreated,
            ...config
        };
        return new EsriLayerListViewModel(props);
    }

    /**
     * Add custom groups
     */
    loadGroups(operationalItems: Collection<ListItem>): void {
        // add groups
        const {config, mapStore, uiStore, view} = this.props;
        const {groups} = config;
        if(!groups || uiStore.layerlistProcessing) {
            return;
        }
        uiStore.setLayerlistProcessing(true);
        if(this.props.config.rebuild && operationalItems.getItemAt(0).layer.type === "map-image"){
            const mapImageLayer = operationalItems.getItemAt(0).layer as __esri.MapImageLayer;
            this.addGroups(mapImageLayer, this.buildHierarchy(groups));
        } else {
            groups.forEach((groupConfig: ListItemConfiguration) => {
                if (!view?.map.layers.some(
                    (l: __esri.Layer) => l.id === groupConfig.layer.id)) {
                    if (groupConfig.hasOwnProperty("title") && !groupConfig.layer.hasOwnProperty("title")) {
                        groupConfig.layer.title = groupConfig.title;
                    }
                    // create group layer
                    const groupLayer = new GroupLayer(groupConfig.layer);
                    // add group layer to the map
                    view?.map.layers.add(groupLayer, groupConfig.index || 0);
                    // register on mobx store to access it more easily
                    mapStore.addGroupLayer(groupLayer.id, groupLayer);
                    // set reference to group layer on layer item
                    groupConfig.layer = groupLayer;
                    // create list item
                    let groupListItem;
                    try {
                        groupListItem = new ListItem(groupConfig);
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }

                    // add list item to the layer list
                    if(groupListItem) {
                        operationalItems.add(groupListItem);
                    }
                    this.props.uiStore.sublayerMap.set(groupConfig.layer.id, groupLayer.get("uid"));

                    // parent layer?
                    if (groupConfig.hasOwnProperty("parent")) {
                        this.addToParent(groupConfig, groupListItem, operationalItems);
                    }
                }
            });
        }
    }
    addGroups(mapImageLayer: __esri.MapImageLayer | __esri.Sublayer, groups: any[]): void {
        groups.forEach((l, index)=>{
            const copy = _.cloneDeep(l.layer);
            copy.sublayers = [];
            copy.title = l.title;
            copy.legendEnabled = false;
            // Nicht benötigte respektive problematische Attribute entfernen
            delete copy.id;
            // Hohe nummerische ID vergeben, damit diese nicht mit einer ID im Map-Service kollidiert
            copy.id = this.nextGroupId;
            this.nextGroupId++;
            const groupLayer = new Sublayer(copy) as ExtendedSublayer;
            if(l.hasOwnProperty("index")) {
                groupLayer.index = l.index;
            } else {
                groupLayer.index = index;
            }
            if(l.hasOwnProperty("limitLevel")) {
                groupLayer.limitLevel = l.limitLevel;
            }
            if(l.layer.hasOwnProperty("visibilityMode")) {
                groupLayer.visibilityMode = l.layer.visibilityMode;
            }
            mapImageLayer.sublayers.add(groupLayer, l.index);
            groupLayer.children = [];
            this.props.uiStore.sublayerMap.set(l.layer.id, groupLayer.get("uid"));
            if(l.sublayers) {
                this.addGroups(groupLayer, l.sublayers);
            }
        });
    }
    buildHierarchy(groups: ListItemConfiguration[]): [] {

        const sublayers = {};
        const roots: [] = [];

        // find the top level nodes and hash the children based on parent
        groups.forEach((groupConfig: ListItemConfiguration) => {
            const p = groupConfig.parent;
            (groupConfig as any).sublayers = [];
            let target: any;
            if (p && p.layer.id) {
                target = (sublayers[p.layer.id] || (sublayers[p.layer.id] = []));
            } else {
                target = roots;
            }
            target.push(groupConfig);
        });

        // function to recursively build the tree
        const findChildren: any = (parent: any) => {
            if (sublayers[parent.layer.id]) {
                parent.sublayers = sublayers[parent.layer.id];
                for (let i = 0, len = parent.sublayers.length; i < len; ++i) {
                    findChildren(parent.sublayers[i]);
                }
            }
        };

        // enumerate through to handle the case where there are multiple roots
        for (let i = 0, len = roots.length; i < len; ++i) {
            findChildren(roots[i]);
        }

        return roots;
    }
    /**
     * Add custom groups
     */
    addItemsToGroup(operationalItems: Collection<ListItem>): void {
        const allItems = operationalItems.flatten((ite: __esri.ListItem) =>
            ite.children
        );
        // add groups (in umgekehrter Reihenfolge hinzufügen)
        this.itemsConfig.sort((a, b) => a.index - b.index).forEach((itemConfig: ListItemConfiguration) => {
            // check if layer has to be moved to a parent (group)
            if (itemConfig.hasOwnProperty("parent")) {
                const oi = allItems.find((item: __esri.ListItem) =>
                    item.layer.id === itemConfig.id
                );
                if (!oi) {
                    return null;
                }
                const { regroupedItems } = this.state;
                if (regroupedItems.has(oi.layer.id)) {
                    return oi;
                }

                this.addToParent(itemConfig, oi, operationalItems);

                // remember the regrouped item
                regroupedItems.set(oi.layer.id, oi);
                this.setState({
                    regroupedItems: regroupedItems
                });
            }
        });
        if(this.props.appConfig.mode !== "production"){
            this.itemsConfig.forEach((itemConfig: ListItemConfiguration) => {
                if(!itemConfig.layer.id){
                    console.warn("Layer ID fehlt:");
                    console.warn(itemConfig.layer);
                }else if(!this.layerIds.includes(itemConfig.layer.id)
                    && !this.props.mapStore.layers.has(itemConfig.layer.id)){
                    // Überprüfung via mapStore ist nicht zuverlässig,
                    // besser wäre via Konfiguration (MapViewerConfiguration.layers)
                    console.warn(`Die Layer ID "${itemConfig.layer.id}" wurde in der Karte nicht gefunden.`);
                }
            });
        }
        this.reorder();
        this.setOpenStatus();
        this.setState({operationalItemsArray: this.operationalItems.flatten(
            (ite: __esri.ListItem) => ite.children)
        });
    }

    /**
     * Add list item to a parent list item
     * Also moves referenced layer to the layers list of the layers list of parent list item layer
     *
     * @param config
     * @param item
     * @param operationalItems
     */
    addToParent(config: ListItemConfiguration, item: ListItem, operationalItems: Collection<ListItem>): void {
        if (config.hasOwnProperty("parent")) {
            let parent;
            if (config.parent.hasOwnProperty("layer")) {
                const parentId = this.props.uiStore.sublayerMap.get(config.parent.layer.id as string);
                parent = operationalItems.flatten((ite: __esri.ListItem) =>
                    ite.children
                ).find((listItem: ListItem) =>
                    listItem.layer?.id === config.parent.layer.id
                    || (listItem.layer as ExtendedLayer)?.uid === parentId);
                if (!parent) {
                    console.error(`No list item found by layer ID: ${config.parent.layer.id}`);
                    return;
                }
            } else if (config.parent.hasOwnProperty("title")) {
                parent = operationalItems.find((listItem: ListItem) =>
                    listItem.title === config.parent.title);
                if (!parent) {
                    console.error(`No list item found by title: ${config.parent.title}`);
                    return;
                }
            }
            if(!parent) {
                return;
            }

            item.parent = parent;
            if("limitLevel" in parent.layer){
                // limitLevel von parent Layer erben.
                const limitLevel: number = parent.layer.get("limitLevel");
                if (limitLevel > 1) {
                    (item.layer as ExtendedLayer).limitLevel = limitLevel - 1;
                }
            }
            if(parent.layer.hasOwnProperty("sublayers")){
                parent.children.add(item, config.index);
                // Layer ID ergänzen, damit dieser auch in der Suche verwendeet werden kann
                (parent.layer as any).children.push(parseInt(item.layer.id, 10));
                if(!parent.layer.visible) {
                    this.itemVisibility.set(item.layer.get("uid"), item.layer.visible);
                    // alle parent Layer berücksichtigen
                    if(item.layer.visible){
                        let p = parent;
                        while(p){
                            this.itemVisibility.set(p.layer.get("uid"), item.layer.visible);
                            p = p.parent;
                        }
                    }
                    item.layer.visible = false;
                }
                operationalItems.getItemAt(0).children.remove(item);
            } else if (parent.layer.hasOwnProperty("layers")) {
                if (parent.layer.type !== "group") {
                    console.error("Referenced parent layer is not a group layer. Cannot add layer to this parent.");
                    return;
                }
                // @ts-ignore --> group layer has property 'layers'
                if (!parent.layer.layers.includes(item.layer)) {
                    // @ts-ignore --> group layer has property 'layers'
                    parent.layer.add(item.layer);
                    parent.children.add(item);
                }
            }
            (item.layer as ExtendedLayer).index = config.index;
            if (config.hasOwnProperty("title")) {
                item.title = config.title;
                item.layer.title = config.title;
            }
        }
    }
    reorder(operationalItems?: Collection<ListItem>): void {
        if(operationalItems === undefined) {
            operationalItems = this.operationalItems;
        } else if((this.props.config.limitLevel === 0 ||
            (this.props.config.limitLevel - this.props.config.startLevel) > 1) && !this.state.hasGroups){
            this.setState({hasGroups: true});
        }
        operationalItems.sort((a, b) =>
            parseInt(a.layer.get("index") ,10) - parseInt(b.layer.get("index"),10));
        operationalItems.forEach((item: __esri.ListItem) => {
            if(item.children.length > 1) {
                this.reorder(item.children);
            }
        });
    }
    setOpenStatus(): void {
        const {config} = this.props;
        const allItems = this.operationalItems.flatten((ite: __esri.ListItem) =>
            ite.children
        );
        this.operationalItems.getItemAt(0).open = true;
        if(config.groups){
            config.groups.forEach((itemConfig: ListItemConfiguration) => {
                const groupId = this.props.uiStore.sublayerMap.get(itemConfig.layer.id as string);
                const groupItem = allItems.find((item: __esri.ListItem) =>
                    item.layer.get("uid") === groupId
                );
                if(groupItem && !this.groupOpenStatusSet.includes(groupId)) {
                    this.groupOpenStatusSet.push(groupId);
                    groupItem.open = itemConfig.open;
                    if(itemConfig.layer.hasOwnProperty("visible")) {
                        groupItem.visible = itemConfig.layer.visible as boolean;
                    }
                }
            });
        }
    }
}
